import { jsx, jsxs } from '@emotion/react/jsx-runtime';
import { compound, createPolymorphicComponent, useSlot, useAdaptiveColorContext, useAdaptiveHeightContext, DisabledEffect, InteractiveTint, FocusRing } from '@nettbureau/core';
import { Icon } from '@nettbureau/icon';
import { Center } from '@nettbureau/layout';
import { ProgressCircle } from '@nettbureau/progress';
import { Rectangle } from '@nettbureau/shape';
import { useCss, inset, parseSpacing } from '@nettbureau/styling';
import { Text } from '@nettbureau/typography';
import { mergeProps } from 'react-aria';
import { useButtonGroupContext } from './button-group-context.js';
import { useButton } from './use-button.js';

/**
 * Buttons allow users to take actions, and make choices, with a single tap.
 */ const Button = compound('Button', createPolymorphicComponent((props, ref)=>{
    const buttonGroupProps = useButtonGroupContext();
    const [mergedProps, mergedRef] = useSlot('Button', {
        ...buttonGroupProps,
        ...props
    }, ref);
    const { as = 'button', css, children, color = useAdaptiveColorContext() ?? 'accent', disabled = false, hitAreaOutset, layout, loading = false, preventFocusOnPress, size = useAdaptiveHeightContext() ?? 'md', stretch = false, variant = 'soft', width = stretch ? '100%' : 'fit-content', ...elementProps } = mergedProps;
    const [{ focusVisible, hovered, pressed }, { buttonProps }] = useButton({
        ...mergedProps,
        as,
        disabled: disabled || loading
    }, mergedRef);
    const variantToShadow = {
        plain: 'none',
        soft: 'none',
        bold: pressed ? 'none' : 'weaker',
        elevated: pressed ? 'weaker' : 'weaker'
    };
    const interacting = hovered || pressed;
    const fillTone = variant === 'plain' ? interacting ? 'soft' : 'none' : variant === 'elevated' ? 'soft' : variant;
    const fontWeight = [
        '2xs',
        'xs',
        'sm',
        'md'
    ].includes(size) ? 'normal' : 'medium';
    const buttonCss = useCss(({ mixins, theme })=>[
            mixins.flexItem({
                shrink: false
            }),
            hitAreaOutset && {
                '&::after': [
                    {
                        content: '""',
                        position: 'absolute'
                    },
                    inset(`-${parseSpacing(theme, hitAreaOutset)}`)
                ]
            },
            loading && {
                cursor: 'default',
                '& > *:not([data-progress-circle-container])': {
                    visibility: 'hidden'
                }
            }
        ]);
    return /*#__PURE__*/ jsx(DisabledEffect, {
        disabled: disabled || loading,
        disableGrayScale: loading,
        children: /*#__PURE__*/ jsx(InteractiveTint, {
            disabled: disabled || loading,
            hovered: hovered,
            pressed: pressed,
            children: /*#__PURE__*/ jsx(FocusRing, {
                color: color,
                focused: focusVisible,
                children: /*#__PURE__*/ jsxs(Rectangle, {
                    "data-button": true,
                    ...mergeProps(filterShapeProps(elementProps), buttonProps),
                    ref: mergedRef,
                    as: as,
                    css: [
                        buttonCss,
                        css
                    ],
                    fill: variant === 'elevated' ? 'base' : color,
                    fillTone: fillTone,
                    minHeight: size,
                    outline: "neutral",
                    layout: {
                        direction: 'horizontal',
                        justify: 'center',
                        ...layout
                    },
                    shadow: variantToShadow[variant],
                    typography: {
                        color: variant === 'elevated' ? color : undefined,
                        colorTone: 'strong',
                        weight: fontWeight
                    },
                    width: width,
                    children: [
                        typeof children === 'string' ? /*#__PURE__*/ jsx(Text, {
                            children: children
                        }) : children,
                        loading && /*#__PURE__*/ jsx(Center, {
                            "data-progress-circle-container": true,
                            strategy: "absolute",
                            children: /*#__PURE__*/ jsx(ProgressCircle, {
                                size: 6
                            })
                        })
                    ]
                })
            })
        })
    });
}), {
    Icon,
    Label: Text
});
/**
 * Filters out props that are not DOM props.
 *
 * @param props - The props to filter.
 */ function filterShapeProps(props) {
    const { children, color, disabled, layout, loading, onBlur, onFocus, onFocusChange, onHoverStart, onHoverEnd, onHoverChange, onKeyDown, onKeyUp, onPress, onPressUp, onPressChange, onPressStart, onPressEnd, size, stretch, variant, ...otherProps } = props;
    return otherProps;
}

export { Button };
//# sourceMappingURL=button.js.map
